/**
 * Generator slug & product_code.
 *
 * Keduanya UNIQUE di tabel products, jadi tabrakan = error 409 buat owner.
 * Modul ini menghasilkan kandidat deterministik lalu caller menambah suffix
 * sampai bebas (lihat `withUniqueSuffix`).
 */

/**
 * Harga coret dari harga jual.
 *
 * `percent` adalah kenaikan DI ATAS harga jual, bukan potongan darinya:
 * 50.000 dengan 40 → 70.000. Yang dicoret angka 70.000; pembeli tetap
 * membayar 50.000.
 *
 * Hasil dibulatkan ke atas ke ribuan terdekat supaya tidak terlihat seperti
 * angka hasil hitungan (mis. 91.000, bukan 90.999).
 *
 * Return null kalau fitur mati atau hasilnya tidak lebih besar dari harga —
 * mencoret angka yang sama atau lebih kecil hanya membingungkan.
 */
export function strikethroughPrice(price: number, percent: number): number | null {
  if (!Number.isFinite(price) || price <= 0) return null;
  if (!Number.isFinite(percent) || percent <= 0) return null;

  const raw = price * (1 + percent / 100);
  const rounded = Math.ceil(raw / 1000) * 1000;
  return rounded > price ? rounded : null;
}

/**
 * Persen hemat yang BENAR relatif harga coret — bukan `percent` mentahnya.
 *
 * Naik 40% di atas 50.000 menghasilkan 70.000, dan hemat dari 70.000 ke
 * 50.000 adalah 29%, bukan 40%. Menampilkan "40%" di sini akan salah.
 */
export function savingsPercent(price: number, strike: number): number {
  if (strike <= price) return 0;
  return Math.round(((strike - price) / strike) * 100);
}

/** Karakter yang aman dibaca manusia — tanpa 0/O/1/I supaya tidak ambigu. */
const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

/** Buang diakritik: "Café" -> "Cafe", bukan "Caf". */
function stripDiacritics(input: string): string {
  return input.normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

/** "Premium VPN Account — 30 Days!" -> "premium-vpn-account-30-days" */
export function slugify(input: string): string {
  return stripDiacritics(input)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");
}

/**
 * Bangun product_code dari nama: ambil huruf awal tiap kata, angka utuh.
 * "Windows 11 Pro Digital License" -> "W11PDL"
 *
 * Kalau hasilnya terlalu pendek (nama satu kata / non-latin), jatuh ke
 * potongan alfanumerik nama, lalu ke kode acak.
 */
export function deriveProductCode(name: string): string {
  const words = stripDiacritics(name)
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(Boolean);

  let code = words.map((w) => (/^\d+$/.test(w) ? w : (w[0] ?? ""))).join("");

  if (code.length < 3) code = words.join("").slice(0, 8);
  if (code.length < 3) code = randomCode(6);

  return code.slice(0, 24);
}

/** Kode acak — fallback sekaligus suffix anti-tabrakan. */
export function randomCode(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i++) {
    out += CODE_ALPHABET[bytes[i]! % CODE_ALPHABET.length];
  }
  return out;
}

/**
 * Coba `base`, lalu `base-2`, `base-3`, ... sampai `isTaken` bilang bebas.
 *
 * Setelah `maxTries` percobaan berurutan, ganti ke suffix acak — kalau ada
 * puluhan produk bernama mirip, menghitung satu per satu boros query dan
 * tetap rawan balapan. UNIQUE constraint di DB tetap jadi backstop terakhir.
 */
export async function withUniqueSuffix(
  base: string,
  isTaken: (candidate: string) => Promise<boolean>,
  opts: { separator?: string; maxTries?: number } = {},
): Promise<string> {
  const separator = opts.separator ?? "-";
  const maxTries = opts.maxTries ?? 5;

  if (!(await isTaken(base))) return base;

  for (let n = 2; n <= maxTries; n++) {
    const candidate = `${base}${separator}${n}`;
    if (!(await isTaken(candidate))) return candidate;
  }

  for (let attempt = 0; attempt < 5; attempt++) {
    const candidate = `${base}${separator}${randomCode(4)}`;
    if (!(await isTaken(candidate))) return candidate;
  }

  throw new Error(`Cannot find unique value for "${base}" after 10 attempts`);
}
